// src/components/atomic-crm/deals/DealShow.tsx
import { formatDistance } from "date-fns";
import { Activity, StickyNote, Receipt, Rss, Users } from "lucide-react";
import {
  RecordContextProvider,
  ShowBase,
  useGetList,
  useListContext,
  useShowContext,
  useTranslate,
  useLocale,
  useNotify,
  useRedirect,
} from "ra-core";
import { useEffect } from "react";
import {
  Link as RouterLink,
  useLocation,
  useMatch,
  useNavigate,
} from "react-router-dom";
import { Card, CardContent } from "@/components/ds/ui/card";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ds/ui/tabs";
import { ReferenceManyField } from "@/components/ds/admin/reference-many-field";
import { Avatar } from "../contacts/Avatar";
import { TagsList } from "../contacts/TagsList";
import { InvoiceCard } from "../invoices";
import { getDateFnsLocale } from "@/i18n/date-fns";
import { ActivityStream } from "../activities/ActivityStream.tsx";
import type { Contact, Deal, Invoice } from "../types";
import { DealAside } from "./DealAside";
import type { Identifier } from "ra-core";  // cleanest

export const DealShow = () => (
  <ShowBase>
    <DealShowContent />
  </ShowBase>
);

const DealContacts = ({ contactIds }: { contactIds: Identifier[] }) => {
  const translate = useTranslate();
  const locale = useLocale();
  const location = useLocation();

  const {
    data: contacts,
    isPending,
    error,
  } = useGetList<Contact>(
    "contacts_summary",
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: "last_name", order: "ASC" },
      filter: contactIds.length ? { "id@in": `(${contactIds.join(",")})` } : {},
    },
    { enabled: contactIds.length > 0 },
  );

  if (!contactIds.length)
    return (
      <p className="p-4 text-sm text-muted-foreground">
        {translate("crm.deal.no_contacts", {
          _: "No contacts linked to this deal.",
        })}
      </p>
    );

  if (isPending) return <div className="p-4 text-sm">Loading…</div>;
  if (error)
    return (
      <div className="p-4 text-sm text-destructive">
        Error loading contacts: {error.message}
      </div>
    );
  if (!contacts?.length)
    return (
      <div className="p-4 text-sm text-muted-foreground">
        No contacts found.
      </div>
    );

  const now = Date.now();
  return (
    <div className="pt-0">
      {contacts.map((contact) => (
        <RecordContextProvider key={contact.id} value={contact}>
          <div className="p-0 text-sm">
            <RouterLink
              to={`/contacts/${contact.id}/show`}
              state={{ from: location.pathname }}
              className="flex items-center justify-between hover:bg-muted py-2 transition-colors"
            >
              <div className="mr-4">
                <Avatar />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">
                  {`${contact.first_name} ${contact.last_name}`}
                </div>
                <div className="text-sm text-muted-foreground truncate">
                  {contact.title}
                  {contact.nb_tasks
                    ? ` - ${translate("crm.task.field.task", {
                        count: contact.nb_tasks,
                        smart_count: contact.nb_tasks,
                      })}`
                    : ""}
                  &nbsp;&nbsp;
                  <TagsList />
                </div>
              </div>
              {contact.last_seen && (
                <div className="text-right shrink-0">
                  <div className="text-sm text-muted-foreground">
                    {translate("crm.common.last_activity", {
                      distance: formatDistance(contact.last_seen, now, {
                        locale: getDateFnsLocale(locale),
                      }),
                    })}
                  </div>
                </div>
              )}
            </RouterLink>
          </div>
        </RecordContextProvider>
      ))}
    </div>
  );
};


const DealShowContent = () => {
  const { record, isPending, error } = useShowContext<Deal>();
  const navigate = useNavigate();
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();

  useEffect(() => {
    if (error) {
      notify("Deal not found or has been deleted", { type: "error" });
      redirect("/deals");
    }
  }, [error, notify, redirect]);

  const tabMatch = useMatch("/deals/:id/show/:tab");
  const currentTab = tabMatch?.params?.tab || "activity";

  const handleTabChange = (value: string) => {
    if (value === currentTab) return;
    if (value === "activity") {
      navigate(`/deals/${record?.id}/show`);
      return;
    }
    navigate(`/deals/${record?.id}/show/${value}`);
  };

  if (isPending || !record) return null;

  return (
    <div className="mt-2 flex pb-2 gap-8">
      <div className="flex-1 min-w-0">
        <Card>
          <CardContent>
            <div className="flex items-center mb-3">
              <h5 className="text-xl flex-1 truncate">{record.name}</h5>
            </div>
            <Tabs defaultValue={currentTab} onValueChange={handleTabChange}>
              {/* ✅ Icon-only tabs on mobile */}
              <TabsList className="w-full flex justify-start h-auto p-1 bg-muted/50 gap-1">
                <TabsTrigger value="activity" className="gap-1.5 px-2 sm:px-3">
                  <Activity className="h-4 w-4 shrink-0" />
                  <span className="hidden sm:inline">
                    {translate("crm.common.activity")}
                  </span>
                </TabsTrigger>

                <TabsTrigger value="contacts" className="gap-1.5 px-2 sm:px-3">
                  <Users className="h-4 w-4 shrink-0" />
                  <span className="hidden sm:inline">
                    {translate("crm.common.contacts", {
                      smart_count: record.nb_contacts || 0,
                    })}
                  </span>
                  {record.nb_contacts > 0 && (
                    <span className="text-[10px] bg-primary/20 text-primary rounded-full px-1.5">
                      {record.nb_contacts}
                    </span>
                  )}
                </TabsTrigger>

                <TabsTrigger value="invoices" className="gap-1.5 px-2 sm:px-3">
                  <Receipt className="h-4 w-4 shrink-0" />
                  <span className="hidden sm:inline">
                    {translate("crm.common.invoices", {
                      smart_count: record.nb_invoices || 0,
                    })}
                  </span>
                  {record.nb_invoices > 0 && (
                    <span className="text-[10px] bg-primary/20 text-primary rounded-full px-1.5">
                      {record.nb_invoices}
                    </span>
                  )}
                </TabsTrigger>
              </TabsList>

              {/* Activity — unified stream (notes + tasks) */}
              <TabsContent value="activity" className="pt-2">
                <ActivityStream
                  resource="deals"
                  recordId={record.id}
                  record={record}
                  contactIds={record.contact_ids ?? []}
                />
              </TabsContent>

              {/* Contacts linked to the deal */}
              <TabsContent value="contacts">
                <DealContacts contactIds={record.contact_ids ?? []} />
              </TabsContent>

              {/* Invoices linked to the deal */}
              <TabsContent value="invoices">
                <ReferenceManyField
                  reference="invoices"
                  target="deal_id"
                  sort={{ field: "issue_date", order: "DESC" }}
                >
                  <InvoicesIterator />
                </ReferenceManyField>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      <div className="hidden md:block">
        <DealAside />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Iterators
// ---------------------------------------------------------------------------


const InvoicesIterator = () => {
  const { data: invoices, error, isPending } = useListContext<Invoice>();
  if (isPending || error) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
      {invoices.map((invoice) => (
        <RouterLink key={invoice.id} to={`/invoices/${invoice.id}/show`}>
          <InvoiceCard invoice={invoice} />
        </RouterLink>
      ))}
    </div>
  );
};
